import React, { useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

import bg1 from 'assets/img/icons/spot-illustrations/corner-1.png';
import FalconCloseButton from 'components/common/FalconCloseButton';
import IconButton from 'components/common/IconButton';
import useFiltro from 'hooks/useFiltro';
import useToken from 'hooks/useToken';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StatisticsCard from './StatisticsCard';

import bg3 from 'assets/img/icons/spot-illustrations/corner-3.png';
import ModalSeguimientoLead from '../leads/ModalSeguimientoLead';
import LicenciaCard from './LicenciaCard';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useToast } from 'context/providers/ToastProvider';

const DebouncedInput = ({ value, onChange, delay = 500, ...props }) => {
    const [localValue, setLocalValue] = useState(value || '');

    useEffect(() => {
        setLocalValue(value || '');
    }, [value]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (localValue !== value) {
                onChange(localValue);
            }
        }, delay);
        return () => clearTimeout(handler);
    }, [localValue, delay, onChange, value]);

    return (
        <Form.Control
            {...props}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
        />
    );
};

DebouncedInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    delay: PropTypes.number
};

const Licencias = () => {

    const [licencias, setLicencias] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [tipoLicencias, setTipoLicencias] = useState([]);
    const [idLead, setIdLead] = useState(0);
    const { FiltroLicencia, setFiltroLicencia, resetFiltroLicencia } = useFiltro();

    const [cargando, setCargando] = useState(false);
    const { showErrorToast } = useToast();
    const [showModalFiltro, setShowModalFiltro] = useState(false);
    const [procesandoModal, setProcesandoModal] = useState(false);
    const [showModalSeguimientoLead, setShowModalSeguimientoLead] = useState(false);
    const [numLicAct, setNumLicAct] = useState([]);
    const [numLicPrueba, setNumLicPrueba] = useState([]);
    
    // Estado para licencias seleccionadas
    const [licenciasSeleccionadas, setLicenciasSeleccionadas] = useState([]);

    // Estados para paginación
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const { token, setToken } = useToken();

    const mostrarLicencias = async (page = 1, append = false, filtrosOverride = null) => {
        if (!append) {
            setCargando(true);
        } else {
            setLoadingMore(true);
        }
        setShowModalFiltro(false);

        // Preparar objeto con filtros y paginación
        const filtrosConPaginacion = {
            ...(filtrosOverride || FiltroLicencia),
            pageNumber: page,
            pageSize: pageSize
        };

        const response = await fetch("/api/licencia/getlicencias/?vFiltro=" + encodeURIComponent(JSON.stringify(filtrosConPaginacion)),
            {
                method: 'GET',
                headers:
                {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });
        if (response.ok) {
            const data = await response.json();
            const nuevasLicencias = data['table1'] || [];

            if (append) {
                setLicencias(prev => [...prev, ...nuevasLicencias]);
            } else {
                setLicencias(nuevasLicencias);
                setPageNumber(1);
            }

            // Verificar si hay más datos disponibles
            const totalRecordsFromResponse = data['table2']?.[0]?.totalRecords || nuevasLicencias.length;
            setTotalRecords(totalRecordsFromResponse);
            setHasMoreData((page * pageSize) < totalRecordsFromResponse);
        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            console.log(response);
            if (!append) {
                setLicencias([]);
                setHasMoreData(false);
            }
        }

        if (!append) {
            setCargando(false);
        } else {
            setLoadingMore(false);
        }
        setProcesandoModal(false);
    };

    /**
     * Función para cargar más datos (infinite scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !cargando) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            mostrarLicencias(nextPage, true);
        }
    };

    /**
     * Componente para el trigger de infinite scroll
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = React.useRef();

        React.useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !cargando) {
                        cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, cargando, pageNumber]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más licencias...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    /**
     * Función para alternar la selección de una licencia
     */
    const toggleSeleccionLicencia = (idLicencia) => {
        setLicenciasSeleccionadas(prev => {
            if (prev.includes(idLicencia)) {
                return prev.filter(id => id !== idLicencia);
            } else {
                return [...prev, idLicencia];
            }
        });
    };

    /**
     * Resetear filtros y recargar
     */
    const handleResetFiltros = () => {
        resetFiltroLicencia();
        setPageNumber(1);
        setHasMoreData(true);
        setLicencias([]);
        mostrarLicencias(1, false);
    };

    /**
     * Buscar con filtros y resetear paginación
     */
    const handleBuscar = (filtrosOverride = null) => {
        setPageNumber(1);
        setHasMoreData(true);
        setLicencias([]);
        mostrarLicencias(1, false, filtrosOverride);
    };

    /**
     * Manejar tecla Enter en campos de búsqueda
     */
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleBuscar();
        }
    };

    const obtenerDatosBusqueda = async () => {

        setCargando(true);
        const response = await fetch("/api/licencia/ObtenerDatosBusqueda",
            {
                method: 'GET',
                headers:
                {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            }).catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                console.log('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            const data = await response.json();

            setVendedores(data['table1']);
            setTipoLicencias(data['table2']);
            setNumLicAct(data['table3']);
            setNumLicPrueba(data['table4']);
        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
            console.log(response);
        }
        setCargando(false);
        mostrarLicencias()
    };

    useEffect(() => {
        obtenerDatosBusqueda();
    }, []);



    return (
        <>
            <Card className="mb-1 p-2">
                <Row className="g-2 align-items-sm-center">
                    <Col xs="auto">
                        <img src="/img/licencia96.png" alt="connectCircle" height={30} />
                    </Col>
                    <Col>
                        <h5>
                            Catálogo de Licencias
                        </h5>
                    </Col>
                    <Col sm="auto" className="ms-auto">
                        <Link to="nueva">
                            <Button size="sm" variant="falcon-primary">Nueva Licencia</Button>
                        </Link>
                    </Col>
                </Row>
            </Card>

            <Row className="g-1 mb-1">
                <Col xxl={12} md={12}>
                    <Row className="g-1">
                        <Col sm={12} md={6} lg={3}>
                            <StatisticsCard stat={{
                                title: 'Licencias Servidor',
                                value: numLicAct.length > 0 ? numLicAct[0].iNum : 0,
                                decimal: false,
                                suffix: ' conectadas',
                                prefix: '',
                                valueClassName: 'text-success',
                                badgeBg: 'success',
                                badgeText: 'Hoy',
                                image: bg3,
                                imgLic: "/img/servidor48.png"
                            }} style={{ minWidth: '12rem' }} />
                        </Col>
                        <Col sm={12} md={6} lg={3}>
                            <StatisticsCard stat={{
                                title: 'Licencias Caja',
                                value: numLicAct.length > 1 ? numLicAct[1].iNum : 0,
                                decimal: false,
                                suffix: ' conectadas',
                                prefix: '',
                                valueClassName: 'text-success',
                                badgeBg: 'success',
                                badgeText: 'Hoy',
                                image: bg3,
                                imgLic: "/img/caja48.png"
                            }} style={{ minWidth: '12rem' }} />
                        </Col>
                        <Col sm={12} md={6} lg={3}>
                            <StatisticsCard stat={{
                                title: 'Licencias Nube',
                                value: numLicAct.length > 2 ? numLicAct[2].iNum : 0,
                                decimal: false,
                                suffix: ' conectadas',
                                prefix: '',
                                valueClassName: 'text-primary',
                                badgeBg: 'success',
                                badgeText: 'Hoy',
                                image: bg3,
                                imgLic: "/img/nube48.png"
                            }} style={{ minWidth: '12rem' }} />
                        </Col>
                        <Col sm={12} md={6} lg={3}>
                            <StatisticsCard stat={{
                                title: 'Licencias Prueba',
                                value: numLicPrueba.length > 0 ? numLicPrueba[0].iNum : 0,
                                decimal: false,
                                suffix: ' conectadas',
                                prefix: '',
                                valueClassName: 'text-danger',
                                badgeBg: 'danger',
                                badgeText: 'Hoy',
                                image: bg1,
                                imgLic: "/img/servidor48.png"
                            }} style={{ minWidth: '12rem' }} />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Header className="bg-light">
                    {/* Filtros */}
                    <Form>
                        <Row className="flex-between-center">
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    aria-label="Default select"
                                    value={FiltroLicencia.vNombreVendedor}
                                    onChange={(event) => { 
                                        const nuevoFiltro = { ...FiltroLicencia, vNombreVendedor: event.target.value };
                                        setFiltroLicencia(nuevoFiltro);
                                        handleBuscar(nuevoFiltro);
                                    }}
                                >
                                    <option value="">Todos los vendedores</option>
                                    <option value="NoAsignados">No asignados</option>
                                    {vendedores.map((vendedor, index) => {
                                        return (<option key={index} value={vendedor.vUsuario}>{vendedor.vNombre} {vendedor.vApellidos}</option>)
                                    })}
                                </Form.Select>
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    aria-label="Default select"
                                    onChange={(event) => { 
                                        const nuevoFiltro = { ...FiltroLicencia, idCatTipoLicencia: event.target.value };
                                        setFiltroLicencia(nuevoFiltro);
                                        handleBuscar(nuevoFiltro);
                                    }}
                                    value={FiltroLicencia.idCatTipoLicencia}
                                >
                                    <option value="">Todas las licencias</option>
                                    {tipoLicencias.map((licencia, index) => {
                                        return (<option key={index} value={licencia.idCatTipoLicencia}>{licencia.vDescripcionTipo}</option>)
                                    })}
                                </Form.Select>
                            </Col>

                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    aria-label="Default select"
                                    value={FiltroLicencia.vEstatusLicencia}
                                    onChange={(event) => { 
                                        const nuevoFiltro = { ...FiltroLicencia, vEstatusLicencia: event.target.value };
                                        setFiltroLicencia(nuevoFiltro);
                                        handleBuscar(nuevoFiltro);
                                    }}
                                >
                                    <option value="">Todos los estatus</option>
                                    <option value="prueba">Prueba</option>
                                    <option value="activa">Activa</option>
                                    <option value="sinlicencia">Canceladas</option>
                                    <option value="30">Por vencer (30 días)</option>
                                    <option value="60">Por vencer (60 días)</option>
                                    <option value="90">Por vencer (90 días)</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Select
                                    size="sm"
                                    aria-label="Default select"
                                    value={FiltroLicencia.vEstatusPoliza}
                                    onChange={(event) => { 
                                        const nuevoFiltro = { ...FiltroLicencia, vEstatusPoliza: event.target.value };
                                        setFiltroLicencia(nuevoFiltro);
                                        handleBuscar(nuevoFiltro);
                                    }}
                                >
                                    <option value="">Todas las pólizas</option>
                                    <option value="30">Póliza por vencer (30 días)</option>
                                    <option value="60">Póliza por vencer (60 días)</option>
                                    <option value="90">Póliza por vencer (90 días)</option>
                                    <option value="polizaVencida">Póliza vencida</option>
                                    <option value="PolizaActiva">Póliza activa</option>
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="flex-between-center">
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Group className="col-12">
                                    <DebouncedInput
                                        onChange={(val) => { 
                                            const nuevoFiltro = { ...FiltroLicencia, vNombreCliente: val.toUpperCase() };
                                            setFiltroLicencia(nuevoFiltro);
                                            handleBuscar(nuevoFiltro);
                                        }}
                                        onKeyDown={handleKeyDown}
                                        size="sm"
                                        type="text"
                                        placeholder="Nombre del cliente"
                                        value={FiltroLicencia.vNombreCliente}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Group className="col-12">
                                    <DebouncedInput
                                        onChange={(val) => { 
                                            const nuevoFiltro = { ...FiltroLicencia, vNombreEmpresa: val.toUpperCase() };
                                            setFiltroLicencia(nuevoFiltro);
                                            handleBuscar(nuevoFiltro);
                                        }}
                                        onKeyDown={handleKeyDown}
                                        size="sm"
                                        type="text"
                                        placeholder="Nombre de la empresa"
                                        value={FiltroLicencia.vNombreEmpresa}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                <Form.Group className="col-12">
                                    <DebouncedInput
                                        onChange={(val) => { 
                                            const nuevoFiltro = { ...FiltroLicencia, vBusquedaLicenciaUUID: val.toUpperCase() };
                                            setFiltroLicencia(nuevoFiltro);
                                            handleBuscar(nuevoFiltro);
                                        }}
                                        onKeyDown={handleKeyDown}
                                        size="sm"
                                        type="text"
                                        placeholder="ID Licencia / UUID Licencia o Empresa"
                                        value={FiltroLicencia.vBusquedaLicenciaUUID}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                                {cargando ? <Button
                                    variant="falcon-default"
                                    size="sm"
                                    transform="shrink-3"
                                    disabled
                                >
                                    <Spinner
                                        as="span"
                                        animation="grow"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />&nbsp;
                                    Buscando...
                                </Button>
                                    : <><IconButton
                                        variant="falcon-default"
                                        type="button"
                                        size="sm"
                                        icon="search"
                                        transform="shrink-3"
                                        onClick={handleBuscar}
                                    >
                                        <span className="d-none d-sm-inline-block ms-1">Buscar</span>
                                    </IconButton> <IconButton
                                        variant="falcon-default"
                                        size="sm"
                                        icon="redo-alt"
                                        transform="shrink-3"
                                        className="mx-2"
                                        onClick={handleResetFiltros}
                                    >
                                            <span className="d-none d-sm-inline-block ms-1">Reset</span>
                                        </IconButton></>}
                            </Col>
                        </Row>
                        <Row className="flex-between-center">
                            <Col xs={12} sm={6} md={6} lg={3} className="d-flex align-items-center pe-0 mb-2">
                            </Col>
                        </Row>
                    </Form>
                </Card.Header>

                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="p-4">
                            {[...Array(5)].map((_, index) => (
                                <Card key={index} className="mb-3">
                                    <Card.Body>
                                        <Row>
                                            <Col xs={12} md={2}>
                                                <Skeleton height={30} width={80} className="mb-2" />
                                                <Skeleton height={40} width={40} className="mb-2" />
                                                <Skeleton height={15} width={60} />
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Skeleton height={20} className="mb-2" />
                                                <Skeleton height={15} width={200} />
                                                <Skeleton height={15} width={180} />
                                                <Skeleton height={15} width={150} />
                                            </Col>
                                            <Col xs={12} md={4} className="text-end">
                                                <Skeleton height={30} width={120} className="mb-2" />
                                                <Skeleton height={25} width={100} />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    ) : licencias.length > 0 ? (
                        <div className="p-3">
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Mostrando {licencias.length} de {totalRecords > 0 ? totalRecords : licencias.length} licencias
                                </small>
                                {licenciasSeleccionadas.length > 0 && (
                                    <small className="text-primary fw-bold">
                                        {licenciasSeleccionadas.length} licencia{licenciasSeleccionadas.length !== 1 ? 's' : ''} seleccionada{licenciasSeleccionadas.length !== 1 ? 's' : ''}
                                    </small>
                                )}
                            </div>
                            {licencias.map((licencia) => (
                                <LicenciaCard
                                    key={licencia.idLicencia}
                                    licencia={licencia}
                                    isSelected={licenciasSeleccionadas.includes(licencia.idLicencia)}
                                    onToggleSelection={toggleSeleccionLicencia}
                                />
                            ))}
                            <InfiniteScrollTrigger />
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <div className="mb-3">
                                <img src="/img/licencia96.png" alt="No hay licencias" height={60} className="opacity-50" />
                            </div>
                            <h5 className="text-muted">No se encontraron licencias</h5>
                            <p className="text-muted">
                                No hay licencias registradas con los filtros seleccionados
                            </p>
                            {licencias.length === 0 && (
                                <Link to="nueva">
                                    <Button variant="primary">
                                        <FontAwesomeIcon icon={icon({ name: 'plus', style: "solid" })} className="me-1" />
                                        Registrar primera licencia
                                    </Button>
                                </Link>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal seguimiento lead */}
            <ModalSeguimientoLead
                showModal={showModalSeguimientoLead}
                setShowModal={setShowModalSeguimientoLead}
                idLead={idLead}
                setIdLead={setIdLead}
            />

            {showModalFiltro &&
                <Modal
                    show={showModalFiltro}
                    onHide={() => setShowModalFiltro(false)}
                    size="lg"
                    backdrop="static"
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                >
                    <Modal.Header>
                        <Modal.Title>Filtro de búsqueda. </Modal.Title>
                        <FalconCloseButton className="btn btn-circle btn-sm transition-base" onClick={() => setShowModalFiltro(false)} />
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col xs={12} sm={6} md={6} lg={4} className="d-flex align-items-center mb-3">
                                <Form.Group className="col-12">
                                    <Form.Label>
                                        Estatus Nube Akasia
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        aria-label="Default select"
                                        value={FiltroLicencia.vEstatusNube}
                                        onChange={(event) => { setFiltroLicencia({ ...FiltroLicencia, vEstatusNube: event.target.value }) }}
                                    >
                                        <option value="">Todos los estatus Nube</option>
                                        <option value="nubePorVencer">Nube por vencer (90 días)</option>
                                        <option value="nubeVencida">Nube vencida</option>
                                        <option value="nubeActiva">Nube activa</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => { setShowModalFiltro(false); }} variant='primary'>Cerrar</Button>
                        {procesandoModal ? <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="grow"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            Procesando...
                        </Button> : <Button type="submit" onClick={() => mostrarLicencias()} variant='success' >Aplicar filtro</Button>}
                    </Modal.Footer>
                </Modal>
            }
        </>
    );
};

export default Licencias;
